import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { kundenAPI } from '../services/api';
import { ArrowLeft, Edit, Trash2, Building2, Phone, Mail, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';

const KundeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [kunde, setKunde] = useState(null);
  const [anlagen, setAnlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [kundeData, anlagenData] = await Promise.all([
        kundenAPI.getById(id),
        kundenAPI.getAnlagen(id)
      ]);
      setKunde(kundeData);
      setAnlagen(anlagenData);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await kundenAPI.delete(id);
      navigate('/kunden');
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Kunden');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Laden...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!kunde) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-lg text-red-600">Kunde nicht gefunden</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <button
          onClick={() => navigate('/kunden')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück zur Übersicht
        </button>

        <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-4 rounded-xl">
                <Building2 className="w-10 h-10 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900" data-testid="kunde-title">{kunde.firma}</h1>
                <p className="text-gray-600 mt-1">Kundennummer: {kunde.kundennummer}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Link
                to={`/kunde/${id}/bearbeiten`}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
                data-testid="edit-button"
              >
                <Edit className="w-4 h-4" />
                Bearbeiten
              </Link>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition"
                data-testid="delete-button"
              >
                <Trash2 className="w-4 h-4" />
                Löschen
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
            {kunde.ansprechpartner && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Ansprechpartner</h3>
                <p className="text-gray-900">{kunde.ansprechpartner}</p>
              </div>
            )}

            {(kunde.strasse || kunde.plz || kunde.ort) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Adresse
                </h3>
                <div className="text-gray-900">
                  {kunde.strasse && <p>{kunde.strasse}</p>}
                  {(kunde.plz || kunde.ort) && <p>{kunde.plz} {kunde.ort}</p>}
                  {kunde.land && kunde.land !== 'Deutschland' && <p>{kunde.land}</p>}
                </div>
              </div>
            )}

            {kunde.telefon && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Telefon
                </h3>
                <a href={`tel:${kunde.telefon}`} className="text-blue-600 hover:text-blue-700">
                  {kunde.telefon}
                </a>
              </div>
            )}

            {kunde.email && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  E-Mail
                </h3>
                <a href={`mailto:${kunde.email}`} className="text-blue-600 hover:text-blue-700">
                  {kunde.email}
                </a>
              </div>
            )}

            {kunde.notizen && (
              <div className="md:col-span-2">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notizen</h3>
                <p className="text-gray-900 whitespace-pre-wrap">{kunde.notizen}</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Anlagen ({anlagen.length})</h2>
            <Link
              to={`/anlage/neu?kunde=${id}`}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition"
            >
              Anlage hinzufügen
            </Link>
          </div>

          {anlagen.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-4">Diesem Kunden sind noch keine Anlagen zugeordnet</p>
              <Link
                to={`/anlage/neu?kunde=${id}`}
                className="inline-block bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition"
              >
                Erste Anlage hinzufügen
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="anlagen-list">
              {anlagen.map((anlage) => (
                <Link
                  key={anlage.id}
                  to={`/anlage/${anlage.id}`}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                >
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded mb-2">
                    {anlage.anlagentyp}
                  </span>
                  <h3 className="font-semibold text-gray-900">{anlage.hersteller}</h3>
                  <p className="text-sm text-gray-600">{anlage.modell}</p>
                  <p className="text-xs text-gray-500 mt-2">SN: {anlage.seriennummer}</p>
                </Link>
              ))}
            </div>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Kunde löschen?</h3>
              <p className="text-gray-600 mb-6">
                Möchten Sie diesen Kunden wirklich löschen? Die Anlagen werden nicht gelöscht, 
                aber die Kundenzuordnung wird entfernt.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition"
                >
                  Ja, löschen
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KundeDetails;
